import getConfig from 'next/config'
import { useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { OPTIMIZELY_FLAGS } from 'src/constants'
import styled from 'styled-components'

import {
  AwazeThemeProvider,
  Brand,
} from '@awaze/design-system.components.awaze-theme-provider'
import { CountdownBanner } from '@awaze/design-system.components.countdown-banner'
import { ResponsiveHeader } from '@awaze/design-system.compositions.responsive-header'
import { useDisclosure } from '@awaze/design-system.utils.react'
import { PlacesProvider } from '@awaze/mfe-common.api.places-api'
import { getBrandConfig } from '@awaze/mfe-common.configs.brand'
import type {
  IntlProviderProps,
  Languages,
} from '@awaze/mfe-common.contexts.intl'
import { IntlProvider } from '@awaze/mfe-common.contexts.intl'
import { SearchContext } from '@awaze/mfe-common.contexts.search'
import { TmsProvider } from '@awaze/mfe-common.contexts.tms'
import { parseItems } from '@awaze/mfe-common.lib.contentful/api/utils/utils_legacy'
import {
  Optimizely,
  getExperimentVariables,
  getExperimentVariationKey,
  getOptimizelyUserId as getHeaderValueFromCookiesOrResponse,
} from '@awaze/mfe-common.lib.optimizely'
import { parseForceDecisionsFromUrlQuery } from '@awaze/mfe-common.lib.optimizely/dist/utils/parseForceDecisionsFromUrlQuery'
import type { SearchCriteria } from '@awaze/mfe-common.types-and-mocks.site-search-service'
import { Footer } from '../components/Footer/Footer'

import { IFooterLabels } from '@awaze/design-system.compositions.footer/dist/footer.types'
import { parseConfig } from '@awaze/mfe-common.lib.contentful-parse-search-form-config'
import CustomHead from 'src/components/CustomHead'
import { SearchFormComponent } from 'src/components/SearchForm'
import { mapSearchCriteriaToInitialValues } from 'src/components/SearchForm/SearchForm.helper'
import { onDesktop, onTablet } from 'src/components/utils/dimension'
import {
  CORRELATION_ID_COOKIE_NAME,
  DEFAULT_PAGE_NUMBER,
  OPTIMIZELY_FLAGS_ENUM,
  WITH_FLEXIBILITY_SET_TO_1,
} from 'src/constants'
import SearchLayout from 'src/layouts/SearchLayout'
import { ApplicationConfigContext } from 'src/lib/applicationConfig'
import { ContentfulFetcher } from 'src/lib/contentful'
import { DisplayContextProvider } from 'src/lib/display/displayContext'
import { buildStatefromUrlParams } from 'src/lib/state/params'
import {
  ContentfulDataProps,
  ContentfulHeader,
  OptimizelyData,
} from 'src/types'
import { logger } from 'src/utils/Logger/'
import { GlobalStyling } from 'src/utils/globalstyle'
import {
  OldBrand,
  cleanObjectOf,
  createInitialValuesFromConfig,
  getCookie,
  getEndOfDayAsUTCString,
  getHeaderString,
  setCookie,
} from 'src/utils/helpers'
import { typeCheckBrand } from 'src/utils/typeCheckBrand'

import { User } from '@awaze/mfe-common.contexts.guest-auth'
import { withServerSideProps } from '@awaze/mfe-common.contexts.guest-auth/pages/getServerSideProps'
import { useAgencyParamTracker } from '@awaze/mfe-common.utils.agency-param-tracker'
import { ParsedUrlQuery, stringify } from 'querystring'
import { OptimizelyDecisionContextProvider } from 'src/context/optimizely-decisions-context'
import { parseDisplayMode } from 'src/lib/display/displayModeParser'
import { createLastMinuteDynamicURL } from 'src/utils/dynamicUrl'
import { setupOptimizely } from 'src/utils/optimizely'
import { unwrapMenu } from 'src/utils/responsiveHeader/responsiveHeaderMap'
import { createFullContextQuery } from '../utils/createFullContextQuery'
import { serverSideSortOrderParameterForExp } from '../__experimental__/GD_158_CDC_Sort_Order_Lates/serverSideSortOrderParameterForExp'
import { getSortOrderValueFromExpKey } from 'src/__experimental__/GD_158_CDC_Sort_Order_Lates/getSortOrderValueFromExpKey'
import { handleInitialSortOptionsConfig } from 'src/utils/sortOptions/handleInitialSortOptions'

interface IndexPageProps {
  brand: Brand
  themeBrand: Brand
  currency: string
  searchCriteria: SearchCriteria
  language: Languages
  contentfulLocale: string
  host: string
  country: string
  contentfulData: ContentfulDataProps
  marketCode: string | null
  optimizelyData?: OptimizelyData
  translations: IntlProviderProps['translations']
  userAgent?: string
  userDeviceType: string
  correlationId?: string
  query: ParsedUrlQuery
  isGuestApp?: boolean
  dynamicQuery: ParsedUrlQuery
  showPhoneIcon?: boolean
  user?: User
  bookingRangeValue: 'late' | 'lastMinute' | 'default'
}

const Content = styled.div`
  ${onTablet} {
    margin-top: 8px;
  }
  ${onDesktop} {
    margin-top: 16px;
  }
`

export default function IndexPage({
  brand,
  currency,
  country,
  searchCriteria,
  contentfulData,
  language,
  contentfulLocale,
  host,
  marketCode,
  optimizelyData,
  themeBrand,
  translations,
  userDeviceType,
  correlationId,
  userAgent,
  query,
  isGuestApp,
  dynamicQuery,
  showPhoneIcon,
  user,
  bookingRangeValue,
}: IndexPageProps): JSX.Element {
  const { publicRuntimeConfig } = getConfig()
  const { APP_ENV } = publicRuntimeConfig
  const placesApiUrl = publicRuntimeConfig[APP_ENV]?.PLACES_API_URI
  const placesAkamaiKey = publicRuntimeConfig[APP_ENV]?.PLACES_API_AKAMAI_KEY
  const { footer } = contentfulData
  const tmsLocale = `${language.toLowerCase()}-${country.toUpperCase()}`
  const tmsCurrency = currency.toUpperCase()
  const queryParams = useSearchParams()

  const initalDisplayMode =
    parseDisplayMode(queryParams?.get('displayMode') ?? 'LIST') ?? 'LIST'

  useAgencyParamTracker()

  useEffect(() => {
    fetch('/api/search/clientpagerender', {})
      .then(() => {
        /** empty */
      })
      .catch(() => {
        /** empty */
      })
  }, [])

  useEffect(() => {
    if (typeof newrelic !== 'undefined') {
      newrelic.addPageAction('Client-PageView-Search', {
        userId: optimizelyData?.userId || null,
      })

      newrelic.setCustomAttribute('displayMode', initalDisplayMode)
    }
  }, [])

  useEffect(() => {
    window['optimizely_decision'] = optimizelyData?.decisions ?? {}

    optimizelyData &&
      Optimizely.getInstance({
        dataFile: optimizelyData.dataFile,
        eventBatchSize: 1,
        additionalConfig: {
          logLevel: 'ERROR',
        },
      })
        .then(async (optimizelyInst) => {
          typeof newrelic !== 'undefined' &&
            newrelic.addPageAction('Optimizely-PageView-Search', {
              userId: optimizelyData.userId,
              ...(correlationId && { correlationId }),
            })

          // Not assigning to a const as we pass the server decisions down to the client and use in context
          // We only call getDecisions here to bucket the user into the experiment client-side
          // We could re-factor this, as we don't need to pass the decisions down from the server
          await optimizelyInst.getDecisions({
            experimentFlags: OPTIMIZELY_FLAGS,
            userId: optimizelyData.userId,
            forceDecisions: parseForceDecisionsFromUrlQuery(query),
            userAttributes: {
              application: 'search-mfe',
              brand,
              host,
              isGuestApp: isGuestApp || false,
              locale: contentfulLocale,
              ...(userAgent && { $opt_user_agent: userAgent, userAgent }),
              userDeviceType,
              sortQueryParam: query.sort?.toString() ?? null,
              initialSearchQueryString: stringify(query),
              accommodationType: query.accommodationType?.toString() ?? null,
              bookingRange: bookingRangeValue,
            },
          })
        })
        .catch((error) => {
          typeof newrelic !== 'undefined' &&
            newrelic.noticeError(
              `Error tracking optimizely age_search_page_view event: ${error}`,
              correlationId ? { correlationId } : null,
            )
        })
  }, [optimizelyData])

  const {
    isOpen: filtersIsOpen,
    open: openFilters,
    close: closeFilters,
  } = useDisclosure()

  const containerRef = useRef()

  if (typeof window !== 'undefined') {
    if (getCookie('onesearch_salt') === null) {
      setCookie('onesearch_salt', Date.now(), getEndOfDayAsUTCString())
    }
  }

  const cleanSearchCriteria: SearchCriteria = cleanObjectOf({
    data: searchCriteria,
    filterOutValues: [null, undefined, ''],
  })

  let searchFormConfig = {
    ...parseConfig(
      {
        ...contentfulData.header.searchForm.config,
      },
      new Date(),
    ),
    showAccommodationField: Boolean(
      contentfulData.header.searchForm.config?.accommodationType,
    ),
  }

  // Since parseconfig from mfe-common and searchform config doesn't align
  // on types, we have to specifically alter the defaultValue of accommodationType
  searchFormConfig = {
    ...searchFormConfig,
    ...(contentfulData.header.searchForm.config?.accommodationTypeDefault && {
      accommodationType: {
        options: searchFormConfig?.accommodationType?.options || [],
        defaultValue:
          cleanSearchCriteria.accommodationType ||
          contentfulData.header.searchForm.config?.accommodationTypeDefault,
      },
    }),
  }

  const cleanedInitialValuesFromConfig = cleanObjectOf({
    data: createInitialValuesFromConfig(searchFormConfig),
    filterOutValues: [null, undefined, ''],
  })

  const useCottageSortConfig =
    cleanSearchCriteria.accommodationType === 'cottages'
  const isDatedSearch = cleanSearchCriteria.start
  const sortConfigBrand = useCottageSortConfig ? 'cottages.com' : brand
  const sortConfig = getBrandConfig(sortConfigBrand).sortOptions

  const isInGD128NovasolV2Exp =
    getExperimentVariationKey(
      optimizelyData?.decisions ?? {},
      OPTIMIZELY_FLAGS_ENUM.GD_128_ONESEARCH_V2,
    ) === 'variant_a'

  const optionsConfig = handleInitialSortOptionsConfig({
    isInNovasolPIMExperiment: isInGD128NovasolV2Exp,
    isDatedSearch,
    sortConfig,
  })

  const recommendedSortVariationKeyHOS = getExperimentVariationKey(
    optimizelyData?.decisions ?? {},
    OPTIMIZELY_FLAGS_ENUM.AGE_SEARCH_RECOMMENDED_SORT_HOS,
  )

  const shouldSortByRecommended = ['variant_a', 'variant_b'].includes(
    recommendedSortVariationKeyHOS,
  )

  let sortValueFromExperiments = optionsConfig.default

  if (shouldSortByRecommended) {
    sortValueFromExperiments = 'recommended'
  }

  const variationKeyForGD156Exp0_14days = getExperimentVariationKey(
    optimizelyData?.decisions ?? {},
    OPTIMIZELY_FLAGS_ENUM.GD_156_0_14_DAYS_SORT_ORDER_SEARCH,
  )

  if (variationKeyForGD156Exp0_14days !== undefined) {
    sortValueFromExperiments = getSortOrderValueFromExpKey(
      variationKeyForGD156Exp0_14days,
      optionsConfig,
    )
  }

  const variationKeyForGD156Exp15_28days = getExperimentVariationKey(
    optimizelyData?.decisions ?? {},
    OPTIMIZELY_FLAGS_ENUM.GD_156_15_28_DAYS_SORT_ORDER_SEARCH,
  )

  const GD156Exp1IsOff =
    variationKeyForGD156Exp0_14days === undefined ||
    variationKeyForGD156Exp0_14days === 'off'

  if (variationKeyForGD156Exp15_28days !== undefined && GD156Exp1IsOff) {
    sortValueFromExperiments = getSortOrderValueFromExpKey(
      variationKeyForGD156Exp15_28days,
      optionsConfig,
    )
  }

  // See ADR 2
  const trueSearchCriteria = {
    ...cleanedInitialValuesFromConfig,
    ...cleanSearchCriteria,
    ...(typeof cleanSearchCriteria.accommodationTypes === 'undefined' && {
      accommodationTypes: [],
    }),
    ...(typeof cleanSearchCriteria.page === 'undefined' && {
      page: DEFAULT_PAGE_NUMBER,
    }),
    ...(typeof cleanSearchCriteria.sort === 'undefined' && {
      sort: sortValueFromExperiments,
    }),
    ...(marketCode && { salesMarket: marketCode }),
    ...dynamicQuery,
    displayMode: initalDisplayMode,
  }

  const trueInitialValues = mapSearchCriteriaToInitialValues(trueSearchCriteria)

  const countdownBannerExp = getExperimentVariationKey(
    optimizelyData?.decisions ?? {},
    OPTIMIZELY_FLAGS_ENUM.AGE_COUNTDOWN_BANNER,
  )

  const isIn2693BannerExp = countdownBannerExp === 'variant_a'

  const bannerVariantData = isIn2693BannerExp
    ? getExperimentVariables(
        optimizelyData?.decisions ?? {},
        OPTIMIZELY_FLAGS_ENUM.AGE_COUNTDOWN_BANNER,
      )?.['banner_text']
    : {}

  const bannerData = isIn2693BannerExp ? bannerVariantData[brand][language] : {}

  return (
    <OptimizelyDecisionContextProvider optimizelyData={optimizelyData ?? {}}>
      <PlacesProvider
        brand={brand}
        language={contentfulLocale}
        placesApiUri={placesApiUrl}
        headers={{ 'x-awaze-akamai': placesAkamaiKey }}
      >
        <IntlProvider
          currency={currency}
          language={language}
          repo="search-mfe"
          translations={translations}
          country={country}
        >
          <TmsProvider
            id={
              isGuestApp
                ? publicRuntimeConfig[APP_ENV]?.guestAppTmsId
                : publicRuntimeConfig[APP_ENV]?.tmsId
            }
            pageLoadDataObject={{
              awaze_device_type: userDeviceType,
              site_environment: APP_ENV,
              site_version: process.env.NEXT_PUBLIC_GIT_HASH,
              site_currency: tmsCurrency,
              site_brand: brand,
              site_sub_brand: '',
              site_locale: tmsLocale,
              page_type: 'search result',
              page_section: 'shop',
              page_name: 'search result',
              page_number: `${trueSearchCriteria.page}`,
              page_id: '',
            }}
          >
            <ApplicationConfigContext.Provider value={{ brand, themeBrand }}>
              <AwazeThemeProvider brand={themeBrand}>
                <DisplayContextProvider
                  props={{ displayMode: initalDisplayMode }}
                >
                  <GlobalStyling />
                  <CustomHead
                    brand={themeBrand}
                    pageMetaTitle={contentfulData.pageMetaTitle}
                    pageMetaDescription={contentfulData.pageMetaTitle}
                    searchCriteria={trueSearchCriteria}
                  />
                  <SearchContext
                    searchCriteria={trueSearchCriteria}
                    brand={brand as OldBrand}
                  >
                    {isIn2693BannerExp &&
                      Object.keys(bannerData).length > 0 && (
                        <CountdownBanner
                          {...bannerData}
                          endDate={new Date(bannerData.endDate)}
                        />
                      )}

                    {!isGuestApp ? (
                      <ResponsiveHeader
                        brand={themeBrand}
                        user={user}
                        menuItems={{
                          mobileMenu: unwrapMenu(
                            contentfulData?.unparsedHeader?.fields?.mobileMenu
                              ?.fields?.menuItems ?? [],
                          ),
                          desktopMenu: unwrapMenu(
                            contentfulData?.unparsedHeader?.fields?.desktopMenu
                              ?.fields?.menuItems ?? [],
                          ),
                        }}
                        labels={{
                          menuButtonText:
                            contentfulData.header.menuButtonText ?? '',
                          closeButtonText:
                            contentfulData.header.closeButtonText ?? '',
                        }}
                        locale={language}
                        showMegaMenu
                        phoneNumber={{
                          displayPhoneIcon: showPhoneIcon,
                          label:
                            contentfulData?.unparsedHeader?.fields?.desktopMenu
                              ?.fields?.menuItems?.[0]?.fields?.title ?? '',
                          href:
                            contentfulData?.unparsedHeader?.fields?.desktopMenu
                              ?.fields?.menuItems?.[0]?.fields?.link?.fields
                              ?.href ?? '',
                        }}
                      />
                    ) : null}
                    <SearchFormComponent
                      defaultConfig={searchFormConfig}
                      initialValues={trueInitialValues}
                      openFilters={openFilters}
                      translations={contentfulData.header.searchForm.labels}
                      locale={contentfulLocale}
                      marketCode={marketCode || '826'}
                      isGuestApp={isGuestApp}
                    />
                    <Content
                      className="content-container"
                      id="content-container"
                      ref={containerRef}
                    >
                      <SearchLayout
                        brand={brand}
                        locale={contentfulLocale}
                        filterControls={{
                          isOpen: filtersIsOpen,
                          open: openFilters,
                          close: closeFilters,
                        }}
                        defaultConfig={searchFormConfig}
                        initialValues={trueInitialValues}
                        translations={{
                          searchformTranslations:
                            contentfulData.header.searchForm.labels,
                        }}
                        experimentDecisions={optimizelyData?.decisions ?? {}}
                        optimizelyUserId={optimizelyData?.userId || ''}
                      />
                      {!isGuestApp ? (
                        <Footer
                          labels={{ ...footer }}
                          brand={themeBrand}
                          currency={currency}
                          country={country}
                        />
                      ) : null}
                    </Content>
                  </SearchContext>
                </DisplayContextProvider>
              </AwazeThemeProvider>
            </ApplicationConfigContext.Provider>
          </TmsProvider>
        </IntlProvider>
      </PlacesProvider>
    </OptimizelyDecisionContextProvider>
  )
}

export const getServerSideProps = withServerSideProps(async (context) => {
  const { headers, cookies, url } = context.req
  const { session } = context
  const { host, referer, 'user-agent': userAgent = null } = headers
  const brand = getHeaderString(headers, 'x-awaze-brand', process.env.BRAND)
  const country = getHeaderString(
    headers,
    'x-awaze-country',
    process.env.COUNTRY,
  )
  const currency = getHeaderString(
    headers,
    'x-awaze-currency',
    process.env.CURRENCY,
  )
  const language = getHeaderString(
    headers,
    'x-awaze-language',
    process.env.LANGUAGE,
  )
  const marketCode = getHeaderString(
    headers,
    'x-awaze-market-code',
    process.env.MARKET_CODE,
  )

  const isGuestApp =
    getHeaderString(headers, 'x-awaze-client', '') === 'guest-app'

  const userDeviceType = getHeaderString(headers, 'user-device-type', null)

  const themeBrand = getHeaderString(headers, 'x-awaze-theme', brand)

  const correlationId = getHeaderValueFromCookiesOrResponse({
    cookies,
    response: context.res,
    cookieName: CORRELATION_ID_COOKIE_NAME,
  })
  if (!correlationId) {
    logger.info('Correlation ID not found in cookies or response headers', {
      request: {
        headers,
        host,
        query: context.query,
        url,
      },
    })
  }

  logger.info('NextJs start getServerSideProps', {
    request: {
      headers,
      host,
      query: context.query,
      url,
    },
    response: {
      statusCode: context.res.statusCode,
    },
    referer,
    ...(correlationId && {
      correlationId,
    }),
  })

  const requiredHeaders = { brand, country, currency, language }
  const requiredEntries = Object.entries(requiredHeaders)

  requiredEntries.forEach(([header, headerValue]) => {
    if (!headerValue) {
      logger.error({
        message: `Request did not contain x-awaze-${header} header`,
        attributes: {
          statusCode: 500,
        },
        ...(correlationId && {
          correlationId,
        }),
      })

      throw new Error(`Request did not contain x-awaze-${header} header`)
    }
  })

  const allowedBrand = typeCheckBrand(brand)
  if (!allowedBrand) {
    logger.error({
      message: `Request contained an invalid x-awaze-brand header: ${brand}`,
      attributes: {
        statusCode: 500,
      },
      ...(correlationId && {
        correlationId,
      }),
    })

    throw new Error(
      `Request contained an invalid x-awaze-brand header: ${brand}`,
    )
  }

  const parsedState = buildStatefromUrlParams(context.query)

  const contentfulFetcher = await ContentfulFetcher.create(
    process.env.NEXT_PUBLIC_API_MOCKING === 'enabled',
  )

  const contentfulLocale = await contentfulFetcher.getLocale(
    Array.isArray(country) ? country[0] : country,
    Array.isArray(language) ? language[0] : language,
  )

  /** HACK: AGE-2272: For France CDC We need cottages header and footer, but we need novasol searchform config */
  const isInFranceCDCExp = themeBrand === 'cottages.com' && brand === 'novasol'
  let novasolSearchFormConfig
  if (isInFranceCDCExp) {
    const novasolData = await contentfulFetcher.getPageData(
      'searchMFE',
      brand,
      contentfulLocale,
    )

    const novasolHeader = parseItems<ContentfulHeader>(novasolData.header)
    const formattedNovasolHeader = Array.isArray(novasolHeader)
      ? novasolHeader[0]
      : novasolHeader

    novasolSearchFormConfig = formattedNovasolHeader.searchForm.config
  }

  const contentfulData = await contentfulFetcher.getPageData(
    'searchMFE',
    themeBrand,
    contentfulLocale,
  )

  const unparsedHeader = contentfulData.header
  const header = parseItems<ContentfulHeader>(contentfulData.header)
  const footer = parseItems<IFooterLabels>(contentfulData.footer)

  const formattedContentfulData = {
    name: 'Search Page',
    slug: contentfulData.slug,
    unparsedHeader: unparsedHeader,
    header: Array.isArray(header) ? header[0] : header,
    footer: Array.isArray(footer) ? footer[0] : footer,
  }

  if (isInFranceCDCExp) {
    formattedContentfulData.header.searchForm.config = novasolSearchFormConfig
  }

  logger.info('NextJs getServerSideProps - pre dynamic url', {
    request: {
      headers,
      host,
      query: context.query,
      url,
    },
    response: {
      statusCode: context.res.statusCode,
    },
    referer,
    searchCriteria: parsedState,
    ...(correlationId && {
      correlationId,
    }),
  })

  const dynamicURL = createLastMinuteDynamicURL(context.query)
  const fullContextQuery = createFullContextQuery(context.query, dynamicURL)

  const bookingRangeValue = serverSideSortOrderParameterForExp(
    fullContextQuery.start,
  )

  const optimizelyData = await setupOptimizely({
    brand,
    cookies,
    host,
    isGuestApp: isGuestApp,
    locale: contentfulLocale,
    response: context.res,
    sdkKey: process.env.OPTIMIZELY_SDK_KEY,
    query: fullContextQuery,
    userDeviceType,
    ...(userAgent && { userAgent }),
    ...(correlationId && {
      correlationId,
    }),
    bookingRange: bookingRangeValue,
  })

  // GD-62: Increase Number of Results Search Page
  const gd62VariationKey = getExperimentVariationKey(
    optimizelyData?.decisions ?? {},
    OPTIMIZELY_FLAGS_ENUM.GD_62_INCREASE_PROPERTIES_ON_SEARCH_PAGE,
  )
  const isInGd62 = gd62VariationKey === 'variant_a'

  if (isInGd62) {
    parsedState.size = 24
  } else {
    delete parsedState.size
  }

  // XP - ACQ-1265
  const changeFlexDecision = getExperimentVariationKey(
    optimizelyData?.decisions ?? {},
    OPTIMIZELY_FLAGS_ENUM.AGE_ACQUISITION_CHANGE_FLEX_ON_SEARCH_FROM_3_TO_1,
  )

  if (changeFlexDecision == WITH_FLEXIBILITY_SET_TO_1) {
    formattedContentfulData.header.searchForm.config.flexibilityDefault = 1
    formattedContentfulData.header.searchForm.config.flexibilityOptions[0].options =
      formattedContentfulData.header.searchForm.config.flexibilityOptions[0].options.map(
        (x) => {
          return x.key.includes('+/- 3')
            ? { ...x, key: x.key.replace('3', '1'), value: '1' }
            : x
        },
      )
  }
  // XP END - ACQ-1265

  const { default: translations } = await import(
    `@awaze/mfe-common.contexts.intl/localization/translation-files/search-mfe/${language}.json`
  )

  const { default: translationsMfeCommon } = await import(
    `@awaze/mfe-common.contexts.intl/localization/translation-files/mfe-common/${language}.json`
  )

  const brandsWithIcon = ['novasol', 'fincallorca']
  const showPhoneIcon =
    brandsWithIcon.includes(brand) && contentfulLocale === 'de-DE'

  logger.info('NextJs end getServerSideProps', {
    request: {
      headers,
      host,
      query: fullContextQuery,
      url,
    },
    response: {
      statusCode: context.res.statusCode,
    },
    referer,
    searchCriteria: parsedState,
    ...(correlationId && {
      correlationId,
    }),
    ...(optimizelyData && {
      optimizelyUserId: optimizelyData.userId,
    }),
  })

  return {
    props: {
      brand,
      themeBrand,
      currency,
      country,
      contentfulLocale,
      host,
      language,
      searchCriteria: parsedState,
      contentfulData: formattedContentfulData,
      marketCode: marketCode ? marketCode : null,
      translations: { ...translations, ...translationsMfeCommon },
      userDeviceType,
      ...(optimizelyData && { optimizelyData }),
      ...(userAgent && { userAgent }),
      ...(correlationId && {
        correlationId,
      }),
      query: context.query,
      dynamicQuery: dynamicURL,
      isGuestApp,
      showPhoneIcon,
      user: session?.user || null,
      bookingRangeValue,
    },
  }
})
